import { useState } from 'react'
import type { StrategyAllocationResponse, StrategyMode, StageAllocation } from '../../types/strategy'
import type { RebalanceCheckResponse } from '../../types/rebalance'
import {
  deriveMarketRegime, REGIME_OVERVIEW, STAGE_PLAYBOOK, MODE_PARAMS, getRegimeParams,
  type MarketRegime, type StageName,
} from '../../data/regimePlaybook'

// Map allocation stage names → rebalance action stage names (mirrors parent)
const STAGE_MAP: Record<string, string[]> = {
  STAKE:  [],
  LEND:   [],
  BORROW: ['BORROW'],
  LP:     ['LP'],
  HEDGE:  ['HEDGE'],
  LOOP:   ['LOOP', 'DELEVERAGE'],
}

const MODE_KEY: Record<StrategyMode, 'conservative' | 'balanced' | 'maxEarn'> = {
  CONSERVATIVE_CARRY: 'conservative',
  BALANCED_CARRY: 'balanced',
  MAX_EARN_CARRY: 'maxEarn',
  LOOP_ACCUMULATION: 'maxEarn',
  HYBRID_LOOP_PLUS_LP: 'maxEarn',
}

const MODE_LABEL: Record<StrategyMode, string> = {
  CONSERVATIVE_CARRY: 'Conservative',
  BALANCED_CARRY: 'Balanced',
  MAX_EARN_CARRY: 'Max Earn',
  LOOP_ACCUMULATION: 'Loop Accumulation',
  HYBRID_LOOP_PLUS_LP: 'Hybrid Loop+LP',
}

function fmt(n: number, d = 2) {
  return n.toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d })
}
function usd(n: number) { return '$' + fmt(n) }

const GAP_THRESHOLD = 0.02

interface StageEntry {
  stage: StageAllocation
  reason: 'gap' | 'recommended' | 'rebalance'
}

function getStagesWithActions(
  data: StrategyAllocationResponse,
  rebalance: RebalanceCheckResponse | null,
): StageEntry[] {
  const allStages: StageAllocation[] = [data.stake, data.lend, data.borrow, data.lp, data.hedge, data.loop]
  const entries: StageEntry[] = []

  for (const stage of allStages) {
    const hasGap = stage.gapUsd != null && stage.amountUsd > 0
      && Math.abs(stage.gapUsd) / stage.amountUsd > GAP_THRESHOLD
    const isRecommended = stage.status === 'RECOMMENDED'
    const hasRebalanceActions = (rebalance?.actions ?? [])
      .some(a => (STAGE_MAP[stage.stage] ?? []).includes(a.stage))

    if (hasGap) entries.push({ stage, reason: 'gap' })
    else if (isRecommended) entries.push({ stage, reason: 'recommended' })
    else if (hasRebalanceActions) entries.push({ stage, reason: 'rebalance' })
  }
  return entries
}

function StageSection({
  entry, regime, mode,
}: {
  entry: StageEntry
  regime: MarketRegime
  mode: StrategyMode
}) {
  const [open, setOpen] = useState(true)
  const { stage, reason } = entry
  const name = stage.stage as StageName
  const playbook = STAGE_PLAYBOOK[name]?.[regime]
  const modeParams = MODE_PARAMS[name] ?? []
  const activeKey = MODE_KEY[mode]

  const gapLabel = reason === 'gap' && stage.gapUsd != null
    ? `Gap: ${stage.gapUsd > 0 ? '+' : ''}${usd(Math.abs(stage.gapUsd))}`
    : reason === 'recommended' ? 'Recommended'
    : 'Rebalance'

  const gapColor = reason === 'gap'
    ? (stage.gapUsd ?? 0) > 0 ? 'text-amber-400' : 'text-orange-400'
    : reason === 'recommended' ? 'text-accent-purple'
    : 'text-yellow-300'

  return (
    <div className="border border-fringe-strong rounded-lg overflow-hidden">
      {/* Collapsible header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-hover transition-colors text-left"
      >
        <svg
          viewBox="0 0 12 12"
          className={`w-2.5 h-2.5 text-gray-500 transition-transform shrink-0 ${open ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" strokeWidth="2"
        >
          <polyline points="3,1 9,6 3,11" />
        </svg>
        <span className="text-[11px] font-semibold text-ink uppercase tracking-wide">{name}</span>
        <span className={`text-[10px] font-medium ${gapColor}`}>{gapLabel}</span>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-3 pb-3 flex flex-col gap-2 border-t border-fringe">
          {/* Playbook action */}
          {playbook && (
            <div className="pt-2">
              <p className="text-[11px] font-medium text-ink">{playbook.action}</p>
              <p className="text-[10px] text-ink-muted mt-1 leading-relaxed">{playbook.detail}</p>
            </div>
          )}

          {/* Mode comparison table */}
          {modeParams.length > 0 && (
            <div className="mt-1">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-gray-400">
                    <th className="text-left font-normal pb-0.5"></th>
                    <th className="text-center font-normal pb-0.5">Conservative</th>
                    <th className="text-center font-normal pb-0.5">Balanced</th>
                    <th className="text-center font-normal pb-0.5">Max Earn</th>
                  </tr>
                </thead>
                <tbody>
                  {modeParams.map(p => (
                    <tr key={p.label}>
                      <td className="text-gray-500 py-0.5 pr-2">{p.label}</td>
                      <td className={`text-center py-0.5 ${activeKey === 'conservative' ? 'text-accent-purple font-semibold' : 'text-ink-muted'}`}>
                        {p.conservative}
                      </td>
                      <td className={`text-center py-0.5 ${activeKey === 'balanced' ? 'text-accent-purple font-semibold' : 'text-ink-muted'}`}>
                        {p.balanced}
                      </td>
                      <td className={`text-center py-0.5 ${activeKey === 'maxEarn' ? 'text-accent-purple font-semibold' : 'text-ink-muted'}`}>
                        {p.maxEarn}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function StrategyGuidePanel({
  data, rebalance, mode, onClose,
}: {
  data: StrategyAllocationResponse
  rebalance: RebalanceCheckResponse | null
  mode: StrategyMode
  onClose: () => void
}) {
  const mc = data.marketContext
  const regime = deriveMarketRegime(mc.trendScore, mc.volatilityRegime)
  const overview = REGIME_OVERVIEW[regime]
  const regimeParams = getRegimeParams(regime)
  const stageEntries = getStagesWithActions(data, rebalance)

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-md bg-surface-card border-l border-fringe-strong shadow-2xl flex flex-col h-full overflow-y-auto animate-slide-in-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-fringe-strong shrink-0">
          <span className="inline-block w-2 h-2 rounded-full bg-accent-purple shrink-0" />
          <span className="text-sm font-semibold text-ink flex-1">Strategy Guide</span>
          <button onClick={onClose} className="text-gray-500 hover:text-ink transition-colors p-1">
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/>
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-3 px-4 py-4 flex-1">
          {/* Regime + mode badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${overview.color}`}>
              {overview.label}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-accent-purple/20 text-accent-purple">
              {MODE_LABEL[mode]}
            </span>
          </div>
          <p className="text-[10px] text-gray-500">{overview.description}</p>

          {/* Overview */}
          <div className="bg-surface-hover rounded-lg px-3 py-2">
            <span className="text-[9px] text-gray-500 uppercase tracking-wide font-medium">Overview</span>
            <p className="text-[11px] text-ink mt-1 leading-relaxed">{overview.tagline}</p>
          </div>

          {/* Key regime parameters */}
          <div>
            <span className="text-[9px] text-gray-500 uppercase tracking-wide font-medium px-0.5">Key Regime Parameters</span>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {regimeParams.map(p => (
                <div key={p.label} className="bg-surface-hover rounded-lg px-2 py-1.5 flex flex-col gap-0.5">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">{p.label}</span>
                  <span className="text-[11px] font-semibold text-ink leading-none mt-0.5">{p.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stage sections */}
          {stageEntries.length > 0 ? (
            <div className="flex flex-col gap-2 mt-1">
              {stageEntries.map(entry => (
                <StageSection
                  key={entry.stage.stage}
                  entry={entry}
                  regime={regime}
                  mode={mode}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <span className="text-[11px] text-gray-500">All stages aligned</span>
              <span className="text-[10px] text-gray-400 mt-1">No gaps or recommendations at this time.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
